import * as React from 'react';
import { useCallback, useEffect } from 'react';

import appInfo from 'js/lib/appInfo';
import store from 'js/lib/coursera.store';
import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { AUTHENTICATION_MODAL_MODES, type AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import AuthenticationModal from 'bundles/authentication/shared/components/authentication-modal/AuthenticationModal';
import useBotManager from 'bundles/authentication/shared/hooks/useBotManager';
import { AUTH_PROVIDER_LOCAL_STORAGE_KEY } from 'bundles/page/common/constants';
import { ARKOSE_BYPASS_APP } from 'bundles/user-account/common/constants';
import { sanitizeAuthProvider } from 'bundles/user-account/utils/AuthenticationOptionUtils';

function isAuthModeValid(authMode: string) {
  return Object.values(AUTHENTICATION_MODAL_MODES).includes(authMode);
}

type Props = {
  isEnterprise?: boolean;
  preventModalClose?: boolean;
  redirectOnCloseUrl?: string;
  utilizeWebRedirectOnClose?: boolean;
  preventRegister?: boolean;
  programId?: string;
};

/**
 * AuthenticationOption is a component that handles authentication modal display logic.
 * It manages the auth mode (login/signup) based on URL parameters and app context,
 * and handles Arkose bot protection integration.
 *
 * The component will:
 * - Show authentication modal when needed
 * - Remove invalid auth modes from URL
 * - Handle Arkose bot protection for specific apps
 * - Bypass Arkose for certain pages to prevent initialization conflicts
 *
 * To be used wherever authentication is needed.
 * Note: PageHeader loads the component, so if your page has the header, there is not need to use this.
 */
const AuthenticationOption = (props: Props) => {
  const { isEnterprise, programId, preventModalClose, preventRegister, redirectOnCloseUrl, utilizeWebRedirectOnClose } =
    props;

  const location = useLocation();
  const { query } = location;

  // We need to bypass Arkose initialization on degree and teams pages
  // that include another authentication form with a separate Arkose initialization.
  // Without this bypass, Arkose would be initialized twice, leading to potential race conditions
  // and conflicts with `window.enforcement`.
  // [Design doc](https://docs.google.com/document/d/11rrksObuKCI5hCI5jQeuyDuB3W7vGMK-_0zKd9Xsau0/edit?tab=t.0#bookmark=id.a1s4eggqi9gb)
  const appName = appInfo.get().appName || '';
  const shouldBypassArkose = ARKOSE_BYPASS_APP.includes(appName) || isAuthenticatedUser();

  const { arkoseBotManager, isRetrievingTokenRef, canSwitchArkoseKey } = useBotManager({
    shouldBypassRecaptcha: true,
    shouldBypassArkose,
  });

  const context = {
    arkoseBotManagerContext: shouldBypassArkose
      ? undefined
      : {
          arkoseBotManager,
          isRetrievingTokenRef,
          canSwitchArkoseKey,
        },
  };

  useEffect(() => {
    // check if authProvider exists, if yes, write the sanitized string to localStorage
    if (query?.authProvider) {
      const sanitizedAuthProviderString = sanitizeAuthProvider(query.authProvider);
      if (sanitizedAuthProviderString) {
        // @ts-expect-error TSMIGRATION
        store.setUnserialized(AUTH_PROVIDER_LOCAL_STORAGE_KEY, sanitizedAuthProviderString);
      }
    }
  }, [query?.authProvider]);

  const updateRouterParams = useCallback<() => void>(() => {
    const { pathname, search } = location;
    const searchParams = new URLSearchParams(search);
    searchParams.delete('authMode');
    const newSearch = searchParams.toString();
    const newPath = newSearch ? `${pathname}?${newSearch}` : pathname;

    window.history.replaceState(null, '', newPath);
  }, [location]);

  const getValidAuthMode = useCallback<() => AUTHENTICATION_MODAL_MODES_KEY | null>(() => {
    if (isAuthenticatedUser()) {
      // no need for auth
      return null;
    }

    const authMode = query?.authMode;
    if (!authMode) {
      return null;
    } else if (isAuthModeValid(authMode)) {
      return authMode as AUTHENTICATION_MODAL_MODES_KEY;
    } else {
      if (typeof window !== 'undefined') {
        updateRouterParams();
      }
      return null;
    }
  }, [query?.authMode, updateRouterParams]);

  const authMode = getValidAuthMode();

  if (!authMode) {
    return null;
  } else {
    return (
      <AuthenticationModal
        onCloseRedirectURL={redirectOnCloseUrl}
        utilizeWebRedirectOnClose={utilizeWebRedirectOnClose}
        preventModalClose={preventModalClose}
        preventRegister={preventRegister}
        isEnterprise={isEnterprise}
        defaultMode={authMode}
        programId={programId}
        context={context}
      />
    );
  }
};

export default AuthenticationOption;
